import { PlaceType } from '@global-types/googleAddress.ts';
import type { CommonAddress } from '@models/interfaces/commonAddress';
import { beautify as zipCodeBeautify } from '@tools/zip-code-formatter';

export const streetToString = (address: CommonAddress, withNumber: boolean = true): string => (withNumber ? `${address.number} ` : '') + address.street;

export const addressToString = (address: CommonAddress, withNumber: boolean = true, separator: string = ' '): string => {
  const street: string = streetToString(address, withNumber);
  const zipCode: string = zipCodeBeautify(address.zip_code) as string;
  let result: string = zipCode !== address.zip_code ? zipCode : `${address.zip_code} ${address.city}`;
  if (street) {
    result = street + separator + result;
  }
  return result;
};

export const oneLineAddress = (street: string, city: string, zipcode: string): string => {
  const streetTrim: string = street.trim();
  const formatted: string = zipCodeBeautify(zipcode) as string;
  if (streetTrim.length === 0) {
    return formatted === zipcode ? `${city} ${zipcode}` : formatted;
  }
  return formatted === zipcode ? `${streetTrim}, ${city}` : `${streetTrim}, ${formatted}`;
};

export const getOneLineAddress = (address: CommonAddress | null): string => {
  if (!address) {
    return '';
  }
  return oneLineAddress(`${address.number} ${address.street}`, address.city, address.zip_code);
};

export const redirectToMaps = (addressString: string): void => {
  const encodedAddress: string = encodeURIComponent(addressString);
  const url: string = `https://www.google.com/maps?q=${encodedAddress}`;
  window.open(url, '_blank');
};

export const getDistrict = (postcode: string): number | null => {
  const match = postcode.match(/^(?:75[0-1]|130|690)?(\d{1,2})$/);

  if (!match) {
    return null;
  }

  return parseInt(match[1], 10);
};

export const getGooglePlaceType = (types: string[] | undefined): PlaceType | null => {
  if (!types) {
    return null;
  }

  const matchedType = types.find((type) => Object.values(PlaceType).includes(type as PlaceType));

  if (!matchedType) {
    return null;
  }

  return matchedType as PlaceType;
};

const streetAbbreviations = new Map<string, string>([
  [ 'chemin', 'Chem.' ],
  [ 'avenue', 'Av.' ],
  [ 'boulevard', 'Bd' ],
  [ 'impasse', 'Imp.' ],
  [ 'allée', 'All.' ],
  [ 'allées', 'All.' ],
  [ 'route', 'Rte' ],
  [ 'place', 'Pl.' ],
  [ 'passage', 'Pass.' ],
  [ 'rond-point', 'Rdpt' ],
]);

// Matches any street-type keyword from the map, whole word, case-insensitive (e.g. /\b(chemin|avenue|boulevard|...)\b/gi)
const streetRegex = new RegExp(
  `\\b(${[ ...streetAbbreviations.keys() ].join('|')})\\b`,
  'gi',
);

export const shortenStreet = (street: string): string => {
  if (!street) {
    return '';
  }

  return street.replace(streetRegex, (matched) => {
    const abbreviation = streetAbbreviations.get(matched.toLowerCase());
    return abbreviation ?? matched;
  });
};
